import React from "react";
import {
  defineMessages,
  FormattedMessage as T,
  useIntl,
  IntlShape,
} from "react-intl";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import LockIcon from "@material-ui/icons/Lock";
import PermIdentityIcon from "@material-ui/icons/PermIdentity";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { AxiosError } from "axios";
import { Controller, useForm, ManualFieldError } from "react-hook-form";
import * as yup from "yup";

import { LoginError } from "../AuthProvider";
import FullscreenNotification, {
  FullscreenNotificationProps,
} from "../FullscreenNotification";

import useAuth from "../../hooks/useAuth";
import { Typography } from "@material-ui/core";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  text: {
    marginTop: 30,
    color: theme.palette.grey[600],
  },
  tapAndPlayIcon: {
    width: 64,
    height: 64,
  },
  textField: {
    marginTop: 16,
  },
  submitButton: {
    marginTop: 20,
    marginLeft: "auto",
  },
}));

const msg = defineMessages({
  changePasswordText: {
    id: "firstLogin.text",
    description: "message to explain firs connexion with password change",
    defaultMessage:
      "This is your first connexion, please change your password.",
  },
  matriculeLabel: {
    id: "firstLogin.matricule.label",
    description: "Message displayed to labeled the matricule field.",
    defaultMessage: "Matricule",
  },
  oldPasswordLabel: {
    id: "firstLogin.oldPassword.label",
    description: "Message displayed to labeled the old password field.",
    defaultMessage: "Old password",
  },
  oldPasswordRequired: {
    id: "firstLogin.loginError.oldPasswordRequired",
    description: "Message displayed when the old password is invalid.",
    defaultMessage: "Old password is required",
  },
  newPasswordLabel: {
    id: "firstLogin.newPassword.label",
    description: "Message displayed to new password field.",
    defaultMessage: "New password",
  },
  newPasswordRequired: {
    id: "firstLogin.loginError.newPasswordRequired",
    description: "Message displayed when the new password is invalid.",
    defaultMessage: "New password required",
  },
  newPasswordConfirmLabel: {
    id: "firstLogin.newPasswordConfirm.label",
    description: "Message displayed to new password confirm field.",
    defaultMessage: "Confirm new password",
  },
  newPasswordConfirmRequired: {
    id: "firstLogin.loginError.newPasswordConfirmRequired",
    description: "Message displayed when the new password confirm is invalid.",
    defaultMessage: "Confirmation required",
  },
  showFieldButtonTooltipLabel: {
    id: "firstLogin.showFieldButtonTooltipLabel.label",
    description: "Label of the show field button tooltip.",
    defaultMessage: "Show",
  },
  hideFieldButtonTooltipLabel: {
    id: "firstLogin.hideFieldButtonTooltipLabel.label",
    description: "Label of the hide field button tooltip.",
    defaultMessage: "Hide",
  },
  submitButtonLabel: {
    id: "firstLogin.submitButton.label",
    description: "Label of the submit button.",
    defaultMessage: "Change password",
  },
  [LoginError.UN1]: {
    id: "login.loginError.un1",
    description: "Message displayed when the user is unauthorized.",
    defaultMessage: "Unauthorized",
  },
  [LoginError.UN2]: {
    id: "login.loginError.un2",
    description: "Message displayed when the user is unauthorized.",
    defaultMessage: "Unauthorized",
  },
  [LoginError.UN3]: {
    id: "login.loginError.un3",
    description: "Message displayed when the user is unauthorized.",
    defaultMessage: "Unauthorized",
  },
  loginErrorInvPass: {
    id: "firstLogin.loginError.invalid",
    description: "Message displayed when the old password is incorrect.",
    defaultMessage: "Old password incorrect",
  },
  loginErrorUnknown: {
    id: "login.loginError.unknown",
    description: "Message displayed when the login error is unknown.",
    defaultMessage: "Unknown login error",
  },
});

function getLoginFormSchema(y: typeof yup, t: IntlShape["formatMessage"]) {
  return y.object().shape({
    oldPassword: y.string().required(t(msg.oldPasswordRequired)),
    newPassword: y.string().required(t(msg.newPasswordRequired)),
    newPasswordConfirm: y
      .string()
      .oneOf([y.ref("newPassword"), undefined], "Passwords must match"),
  });
}

type LoginFormValues = yup.InferType<ReturnType<typeof getLoginFormSchema>>;

const emptyLoginFormValues: LoginFormValues = {
  oldPassword: "",
  newPassword: "",
  newPasswordConfirm: "",
};

export interface FirstLoginData {
  matricule: string;
  pinCode: string;
  deviceId: string;
  cardUid: string;
  keepSessionOpen: boolean;
  token: string;
}

export type FirstLoginFormProps = {
  loginData?: FirstLoginData;
};

function FirstLoginForm(props: FirstLoginFormProps) {
  const { loginData } = props;
  const classes = useStyles();
  const { formatMessage: t } = useIntl();
  const { control, errors, setError, formState, handleSubmit } =
    useForm<LoginFormValues>({
      defaultValues: emptyLoginFormValues,
      validationSchema: getLoginFormSchema(yup, t),
    });
  const [notification, setNotification] =
    React.useState<FullscreenNotificationProps>({ open: false });
  const { login, authenticationService } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [showOldPassword, setShowOldPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showNewPasswordConfirmation, setNewPasswordConfirmation] =
    React.useState(false);

  /**
   * Manage the visibility of the old password field.
   */
  const handleClickShowOldPassword = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setShowOldPassword(!showOldPassword);
    },
    [showOldPassword]
  );

  /**
   * Manage the visibility of the new password field.
   */
  const handleClickShowNewPassword = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setShowNewPassword(!showNewPassword);
    },
    [showNewPassword]
  );

  /**
   * Manage the visibility of the confirmation new password field.
   */
  const handleClickShowNewPasswordConfirmation = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setNewPasswordConfirmation(!showNewPasswordConfirmation);
    },
    [showNewPasswordConfirmation]
  );

  const handleNotificationClose = React.useCallback(
    (_event: React.SyntheticEvent<Element, Event>) => {
      setNotification({ open: false });
    },
    []
  );

  /**
   * Takes care of a change password error.
   */
  const processChangePasswordError = React.useCallback((error: AxiosError) => {
    let loginErrorMessage: FullscreenNotificationProps["message"] | undefined;
    switch (error.response?.data.code) {
      case "INVPASS":
        loginErrorMessage = <T {...msg.loginErrorInvPass} />;
        break;
      default:
        loginErrorMessage = <T {...msg.loginErrorUnknown} />;
        break;
    }
    if (loginErrorMessage !== undefined) {
      setNotification({
        open: true,
        message: loginErrorMessage,
        type: "error",
      });
    }
  }, []);

  /**
   * Takes care of a login error.
   */
  const processLoginError = React.useCallback(
    (error: AxiosError) => {
      let fieldErrors: ManualFieldError<LoginFormValues>[] | undefined;
      let loginErrorMessage: FullscreenNotificationProps["message"] | undefined;
      switch (error.response?.data.code) {
        case LoginError.UN1:
          loginErrorMessage = <T {...msg[LoginError.UN1]} />;
          break;
        case LoginError.UN2:
          loginErrorMessage = <T {...msg[LoginError.UN2]} />;
          break;
        case LoginError.UN3:
          loginErrorMessage = <T {...msg[LoginError.UN3]} />;
          break;
        default:
          loginErrorMessage = <T {...msg.loginErrorUnknown} />;
          break;
      }

      if (fieldErrors !== undefined) setError(fieldErrors);
      if (loginErrorMessage !== undefined) {
        setNotification({
          open: true,
          message: loginErrorMessage,
          type: "error",
        });
      }
    },
    [setError]
  );

  /**
   * Takes care of the submit process.
   */
  const handleLoginFormSubmit = React.useCallback(
    (data: LoginFormValues, _event?: React.BaseSyntheticEvent) => {
      if (loginData?.token) {
        setLoading(true);
        let payload = {
          plainPassword: {
            first: data.newPassword,
            second: data.newPasswordConfirm,
          },
          oldPassword: data.oldPassword,
          cardSerialNumber: loginData?.cardUid,
        };
        authenticationService
          .changePassword(loginData?.token, payload)
          .then((_res: any) => {
            if (loginData) {
              login(
                loginData?.matricule,
                data.newPassword,
                loginData?.deviceId,
                loginData?.cardUid,
                loginData?.keepSessionOpen ? "1" : "0"
              )
                .catch((error: AxiosError) => {
                  processLoginError(error);
                })
                .finally(() => setLoading(false));
            }
          })
          .catch((error: AxiosError) => {
            processChangePasswordError(error);
            setLoading(false);
          });
      }
    },
    [
      authenticationService,
      login,
      loginData,
      processChangePasswordError,
      processLoginError,
    ]
  );

  return (
    <form
      className={classes.root}
      onSubmit={handleSubmit(handleLoginFormSubmit)}
    >
      <Typography className={classes.text}>
        {t(msg.changePasswordText)}
      </Typography>
      <TextField
        className={classes.textField}
        value={loginData?.matricule}
        label={t(msg.matriculeLabel)}
        autoFocus
        disabled
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PermIdentityIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      <Controller
        name="oldPassword"
        as={
          <TextField
            className={classes.textField}
            helperText={!!errors.oldPassword && errors.oldPassword.message}
            label={t(msg.oldPasswordLabel)}
            type={showOldPassword ? "text" : "password"}
            required={errors.oldPassword?.type === "required"}
            error={!!errors.oldPassword}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      showOldPassword
                        ? t(msg.hideFieldButtonTooltipLabel)
                        : t(msg.showFieldButtonTooltipLabel)
                    }
                  >
                    <IconButton
                      aria-label={
                        showOldPassword
                          ? t(msg.hideFieldButtonTooltipLabel)
                          : t(msg.showFieldButtonTooltipLabel)
                      }
                      edge="end"
                      onClick={handleClickShowOldPassword}
                    >
                      {showOldPassword ? (
                        <VisibilityIcon color="secondary" />
                      ) : (
                        <VisibilityOffIcon color="secondary" />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        }
        control={control}
      />
      <Controller
        name="newPassword"
        as={
          <TextField
            className={classes.textField}
            helperText={!!errors.newPassword && errors.newPassword.message}
            label={t(msg.newPasswordLabel)}
            type={showNewPassword ? "text" : "password"}
            required={errors.newPassword?.type === "required"}
            error={!!errors.newPassword}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      showNewPassword
                        ? t(msg.hideFieldButtonTooltipLabel)
                        : t(msg.showFieldButtonTooltipLabel)
                    }
                  >
                    <IconButton
                      aria-label={
                        showNewPassword
                          ? t(msg.hideFieldButtonTooltipLabel)
                          : t(msg.showFieldButtonTooltipLabel)
                      }
                      edge="end"
                      onClick={handleClickShowNewPassword}
                    >
                      {showNewPassword ? (
                        <VisibilityIcon color="secondary" />
                      ) : (
                        <VisibilityOffIcon color="secondary" />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        }
        control={control}
      />
      <Controller
        name="newPasswordConfirm"
        as={
          <TextField
            className={classes.textField}
            helperText={
              !!errors.newPasswordConfirm && errors.newPasswordConfirm.message
            }
            label={t(msg.newPasswordConfirmLabel)}
            type={showNewPasswordConfirmation ? "text" : "password"}
            required={errors.newPasswordConfirm?.type === "required"}
            error={!!errors.newPasswordConfirm}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      showNewPasswordConfirmation
                        ? t(msg.hideFieldButtonTooltipLabel)
                        : t(msg.showFieldButtonTooltipLabel)
                    }
                  >
                    <IconButton
                      aria-label={
                        showNewPasswordConfirmation
                          ? t(msg.hideFieldButtonTooltipLabel)
                          : t(msg.showFieldButtonTooltipLabel)
                      }
                      edge="end"
                      onClick={handleClickShowNewPasswordConfirmation}
                    >
                      {showNewPasswordConfirmation ? (
                        <VisibilityIcon color="secondary" />
                      ) : (
                        <VisibilityOffIcon color="secondary" />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        }
        control={control}
      />
      <Button
        className={classes.submitButton}
        color="primary"
        variant="contained"
        type="submit"
        startIcon={
          formState.isSubmitting || loading ? (
            <CircularProgress size={20} />
          ) : (
            <ExitToAppIcon />
          )
        }
        disabled={formState.isSubmitting || loading}
      >
        <T {...msg.submitButtonLabel} />
      </Button>
      <FullscreenNotification
        onCloseButtonClick={handleNotificationClose}
        {...notification}
      />
    </form>
  );
}

export default FirstLoginForm;
